<template>
  <ReportCard :title="$t('report.client_mix_title')" header-margin="mb-4">
    <template #header>
      <button
        @click.stop="showMixInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.client_mix_info.title')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>

    <!-- Current period bar -->
    <div class="mb-3">
      <div class="flex items-baseline gap-1.5 mb-1.5">
        <span class="text-[11px] font-semibold text-muted-foreground">
          {{ $t('report.client_mix_current') }}
        </span>
        <span class="text-[10px] text-muted-foreground/50">
          {{ currentPeriodLabel }}
        </span>
      </div>
      <template v-if="hasCurrentMixData">
        <div class="flex h-5 rounded-full overflow-hidden">
          <div v-for="type in allClientTypeKeys" :key="'mix-cur-' + type"
            class="flex items-center justify-center text-[10px] font-bold text-white transition-all"
            :style="{
              backgroundColor: clientTypeColor(type),
              width: clientMix.current[type] != null ? clientMix.current[type].toFixed(1) + '%' : '0%',
              minWidth: (clientMix.current[type] ?? 0) > 5 ? '32px' : '0px'
            }">
            <span v-if="(clientMix.current[type] ?? 0) > 8">
              {{ Math.round(clientMix.current[type] ?? 0) }}%
            </span>
          </div>
        </div>
        <div class="flex justify-between mt-1">
          <span v-for="type in allClientTypeKeys" :key="'mix-cur-lbl-' + type"
            class="text-[10px] font-semibold"
            :style="{ color: clientTypeColor(type) }">
            {{ type }} {{ (clientMix.current[type] ?? 0).toFixed(1) }}%
          </span>
        </div>
      </template>
      <div v-else class="flex h-5 rounded-full overflow-hidden ct-empty-bar">
        <div class="w-full"></div>
      </div>
    </div>

    <!-- Previous period bar -->
    <div class="mb-3">
      <div class="flex items-baseline gap-1.5 mb-1.5">
        <span class="text-[11px] font-semibold text-muted-foreground">
          {{ $t('report.client_mix_previous') }}
        </span>
        <span class="text-[10px] text-muted-foreground/50">
          {{ previousPeriodLabel }}
        </span>
      </div>
      <div class="flex h-5 rounded-full overflow-hidden">
        <div v-for="type in allClientTypeKeys" :key="'mix-prev-' + type"
          class="flex items-center justify-center text-[10px] font-bold text-white transition-all"
          :style="{
            backgroundColor: clientTypeColor(type),
            width: clientMix.previous[type] != null ? clientMix.previous[type].toFixed(1) + '%' : '0%',
            minWidth: (clientMix.previous[type] ?? 0) > 5 ? '32px' : '0px',
            opacity: 0.7
          }">
          <span v-if="(clientMix.previous[type] ?? 0) > 8">
            {{ Math.round(clientMix.previous[type] ?? 0) }}%
          </span>
        </div>
      </div>
      <div class="flex justify-between mt-1">
        <span v-for="type in allClientTypeKeys" :key="'mix-prev-lbl-' + type"
          class="text-[10px] font-semibold text-muted-foreground/70">
          {{ type }} {{ (clientMix.previous[type] ?? 0).toFixed(1) }}%
        </span>
      </div>
    </div>

    <!-- Shift badges -->
    <div class="flex items-center gap-2 mt-2">
      <EvolutionIndicator v-for="type in allClientTypeKeys" :key="'mix-shift-' + type"
        :value="clientMix.shift[type] ?? 0"
        :formatted="type + ' ' + (clientMix.shift[type] != null && clientMix.shift[type] !== 0
          ? (clientMix.shift[type] > 0 ? '+' : '') + clientMix.shift[type].toFixed(1) + 'pts'
          : '0pts')"
        :show-neutral="true" />
    </div>
  </ReportCard>

  <BottomSheet v-model:is-open="showMixInfo" variant="sidebar">
    <ClientMixInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { SalesByClientTypesResponse, TaxDataKey } from '../../responses'
import { clientTypeColor } from '../../utils/ClientTypeColors'
import ReportCard from '../ReportCard.vue'
import EvolutionIndicator from '../ui/EvolutionIndicator.vue'
import BottomSheet from '../BottomSheet.vue'
import ClientMixInfoSheet from '../ClientMixInfoSheet.vue'

const props = defineProps<{
  response: SalesByClientTypesResponse
  taxKey: TaxDataKey
  currentPeriodLabel: string
  previousPeriodLabel: string
}>()

const showMixInfo = ref(false)

/** Types with current OR previous data */
const allClientTypeKeys = computed<string[]>(() => {
  if (!props.response?.client_types) return []
  return Object.keys(props.response.client_types).filter(type => {
    const data = props.response.client_types[type]
    if (!data?.summary) return false
    const cur = data.summary.total_period.tax_data
    const prev = data.summary.total_previous_period?.tax_data
    return (cur.excluded_tax !== 0 || cur.included_tax !== 0)
      || (prev && (prev.excluded_tax !== 0 || prev.included_tax !== 0))
  })
})

const clientMix = computed(() => {
  const current: Record<string, number> = {}
  const previous: Record<string, number> = {}
  const shift: Record<string, number> = {}
  const types = allClientTypeKeys.value

  let totalCurrent = 0
  let totalPrevious = 0

  for (const type of types) {
    const data = props.response?.client_types[type]
    if (!data?.summary) continue
    totalCurrent += data.summary.total_period.tax_data[props.taxKey]
    totalPrevious += data.summary.total_previous_period?.tax_data[props.taxKey] ?? 0
  }

  for (const type of types) {
    const data = props.response?.client_types[type]
    if (!data?.summary) {
      current[type] = 0
      previous[type] = 0
      shift[type] = 0
      continue
    }
    const curVal = data.summary.total_period.tax_data[props.taxKey]
    const prevVal = data.summary.total_previous_period?.tax_data[props.taxKey] ?? 0

    current[type] = totalCurrent > 0 ? (curVal / totalCurrent) * 100 : 0
    previous[type] = totalPrevious > 0 ? (prevVal / totalPrevious) * 100 : 0
    shift[type] = current[type] - previous[type]
  }

  return { current, previous, shift }
})

const hasCurrentMixData = computed(() => {
  return allClientTypeKeys.value.some(type => (clientMix.value.current[type] ?? 0) > 0)
})
</script>

<style scoped>
/* Empty mix bar */
.ct-empty-bar {
  background: repeating-linear-gradient(
    -45deg,
    rgba(156, 163, 175, 0.08),
    rgba(156, 163, 175, 0.08) 4px,
    transparent 4px,
    transparent 8px
  );
  border: 1px dashed rgba(156, 163, 175, 0.2);
}
:is(.dark *).ct-empty-bar {
  background: repeating-linear-gradient(
    -45deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.03) 4px,
    transparent 4px,
    transparent 8px
  );
  border: 1px dashed rgba(255, 255, 255, 0.1);
}
</style>
